import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Bike, CalendarDays, Clock, HeartHandshake, Hospital, Mic, PoundSterling, Quote, Radio, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { JUSTGIVING } from "@/components/public/PublicLayout";
import { CountUp, Eyebrow, HeroSlideshow, PhotoMarquee, Reveal } from "@/components/public/Motion";
import { PostCard } from "@/pages/public/Posts";

const QUOTES = [
  ["We are so grateful to the volunteer riders who give up their time to courier blood products and urgent samples. Every minute counts when it comes to helping seriously-ill and injured patients.", "Paula Gardener", "Chief Nurse, Burton Hospital Trust"],
  ["There are certainly times when the quicker we can get results back, the better the outcome for the patient, so blood bikes are helping to save lives and, at the same time, saving the NHS lots of money.", "Debbie Jones", "Shrewsbury and Telford Hospital NHS Trust"],
  ["The blood bikes are vital to getting our life saving faecal transplants to critically ill patients with C. difficile infection.", "Professor Peter Hawkey", "FMT Service Lead, PHE Public Health Laboratory Birmingham"],
  ["The voluntary service the blood bikes offer us has been instrumental in widening the scope of our FMT service across the country and ensuring costs are as low as possible for the NHS.", "Dr Vicki McCune", "FMT Service Manager, PHE Birmingham"],
];

const HEADLINES = [
  ["When every minute counts,", "we ride."],
  ["Day or night,", "blood gets through."],
  ["Volunteers on two wheels.", "Lifelines on the road."],
  ["Free for the NHS.", "Priceless for patients."],
  ["365 days a year,", "the bikes keep rolling."],
  ["Your donation fuels", "the next urgent run."],
  ["Ordinary people.", "Extraordinary miles."],
];

const RotatingHeadline = () => {
  const [i, setI] = useState(0);
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setShow(false);
      setTimeout(() => { setI((x) => (x + 1) % HEADLINES.length); setShow(true); }, 600);
    }, 4800);
    return () => clearInterval(t);
  }, []);
  const [a, b] = HEADLINES[i];
  return (
    <h1 data-testid="hero-headline" data-index={i} aria-live="polite" className={`mt-4 min-h-[2.9em] max-w-4xl text-5xl font-black leading-[0.95] tracking-tight transition-[opacity,transform,filter] duration-500 sm:min-h-[1.95em] sm:text-6xl lg:text-8xl ${show ? "translate-y-0 opacity-100 blur-0" : "translate-y-4 opacity-0 blur-sm"}`}>
      {a} <span className="text-[#FEDC19]">{b}</span>
    </h1>
  );
};

const Hero = ({ stats }) => (
  <section className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="public-hero">
    <HeroSlideshow />
    <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-32">
      <Reveal><Eyebrow>Shropshire · Staffordshire · Cheshire</Eyebrow></Reveal>
      <RotatingHeadline />
      <Reveal delay={240}><p className="mt-6 max-w-xl text-base text-white/80 md:text-lg">Volunteer blood bikers and blood cars carrying urgent blood, samples and supplies to NHS hospitals, free of charge, day and night, 365 days a year.</p></Reveal>
      <Reveal delay={360} className="mt-8 flex flex-wrap gap-3">
        <Link to="/volunteer#apply" data-testid="hero-volunteer-btn" className="inline-flex items-center gap-2 rounded-full bg-[#FEDC19] px-7 py-3.5 font-bold text-black transition-transform hover:scale-105">Volunteer with us <ArrowRight className="h-4 w-4" /></Link>
        <a href={JUSTGIVING} target="_blank" rel="noreferrer" data-testid="hero-donate-btn" className="inline-flex items-center gap-2 rounded-full bg-[#DB0000] px-7 py-3.5 font-bold text-white transition-transform hover:scale-105"><HeartHandshake className="h-4 w-4" /> Donate</a>
      </Reveal>
      <div className="mt-14 grid max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl sm:grid-cols-4" data-testid="hero-stats">
        {[["24/7", "Service, 365 days"], [3, "Counties served"], [stats?.vehicles || 20, "Bikes & blood cars"], [300, "£ saved per urgent trip, up to"]].map(([n, l]) => (
          <div key={l} className="bg-[#0d0e11]/60 p-5"><div className="text-3xl font-black text-[#FEDC19]">{typeof n === "number" ? <CountUp to={n} /> : n}</div><div className="mt-1 text-xs text-white/70">{l}</div></div>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" className="mx-auto grid max-w-7xl scroll-mt-20 gap-12 px-5 py-24 lg:grid-cols-2">
    <Reveal>
      <Eyebrow>What we do</Eyebrow>
      <h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">A lifeline between hospitals, run entirely by volunteers.</h2>
      <p className="mt-6 text-white/75">Whether it's the early hours of the morning or the end of the night, if a hospital urgently needs blood, our blood bikers are there. Hospitals would otherwise pay taxi services up to £300 a trip for the same job. Working with the Nationwide Association of Blood Bikes, that money is saved for things like medical supplies.</p>
      <p className="mt-4 text-white/75">We can only do it with the support of our local community. A donation or a few hours of your time keeps our bikes on the road.</p>
    </Reveal>
    <div className="grid gap-4 sm:grid-cols-2">
      {[[Hospital, "NHS hospitals", "Blood products, samples, breast milk and urgent supplies between hospitals and labs."], [Clock, "Round the clock", "Morning, afternoon, evening and night shifts, every day of the year."], [Radio, "Controlled & tracked", "Duty controllers take hospital calls and dispatch the nearest rider."], [Award, "Award winning", "Holder of the Queen's Award for Voluntary Service."]].map(([I, t, d], n) => (
        <Reveal key={t} delay={n * 100} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:border-[#FEDC19]/60">
          <I className="h-8 w-8 text-[#FEDC19]" /><h3 className="mt-4 text-lg font-bold">{t}</h3><p className="mt-2 text-sm text-white/65">{d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Impact = ({ stats }) => (
  <section className="relative overflow-hidden border-y border-white/10 bg-[#FEDC19] text-black" data-testid="impact-counter">
    <div className="mx-auto max-w-7xl px-5 py-16">
      <Reveal className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em]"><span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#DB0000] opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#DB0000]" /></span>Live from our rota</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Every run logged by our volunteers.</h2></div>
        <p className="max-w-sm text-sm text-black/70" data-testid="impact-note">{stats && !stats.runs ? "Our live tally starts with the first run logged in the new SCBB management system. Watch this space." : "Totals update automatically each time a rider completes a run in the SCBB management system."}</p>
      </Reveal>
      <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-black/15 lg:grid-cols-4">
        {[["runs", "Urgent runs completed"], ["miles", "Miles ridden & driven"], ["runs_year", `Runs in ${stats?.year || new Date().getFullYear()}`], ["miles_year", `Miles in ${stats?.year || new Date().getFullYear()}`]].map(([k, l]) => (
          <div key={k} className="bg-[#FEDC19] p-6" data-testid={`impact-${k}`}>
            <div className="text-4xl font-black tabular-nums sm:text-5xl">{stats ? <CountUp to={stats[k] || 0} /> : "—"}</div>
            <div className="mt-2 text-xs font-bold uppercase tracking-wider text-black/65">{l}</div>
          </div>
        ))}
      </div>
      <Counties list={stats?.counties} />
    </div>
  </section>
);

const Counties = ({ list }) => list?.length > 0 && (
  <div className="mt-10" data-testid="impact-counties">
    <p className="text-xs font-bold uppercase tracking-[0.3em] text-black/70">Your county, your lifeline</p>
    <div className="mt-4 grid gap-3 sm:grid-cols-3">
      {list.map((c, n) => (
        <Reveal key={c.county} delay={n * 90} className="rounded-2xl bg-black p-5 text-white">
          <div className="text-sm font-bold text-[#FEDC19]" data-testid={`impact-county-${c.county.toLowerCase()}`}>{c.county}</div>
          <div className="mt-3 flex items-end gap-6">
            <div><div className="text-3xl font-black tabular-nums"><CountUp to={c.runs} /></div><div className="text-[11px] uppercase tracking-wider text-white/60">Runs</div></div>
            <div><div className="text-3xl font-black tabular-nums"><CountUp to={c.miles} /></div><div className="text-[11px] uppercase tracking-wider text-white/60">Miles</div></div>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

const Roles = () => (
  <section className="relative overflow-hidden bg-[#121318] py-24" data-testid="home-roles">
    <div className="mx-auto max-w-7xl px-5">
      <Reveal><Eyebrow>Want to volunteer?</Eyebrow><h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Find the role for you.</h2></Reveal>
      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {[["/site/riders.jpg", Bike, "Riders & drivers", "Carry urgent samples on a liveried bike or blood car. Advanced certificate required.", "/volunteer#apply"], ["/site/controller.jpg", Radio, "Controllers", "Take hospital calls and dispatch riders from home. Full training given.", "/volunteer#apply"], ["/site/fundraising.jpg", PoundSterling, "Fundraisers", "Attend or run events that keep the fleet fuelled and free for hospitals.", "/volunteer#fundraisers"], ["/site/volunteer3.jpg", Mic, "Speaker team", "Share our stories with groups across the three counties.", "/volunteer#speakers"]].map(([img, I, t, d, to], n) => (
          <Reveal key={t} delay={n * 90}>
            <Link to={to} data-testid={`role-card-${n}`} className="group block overflow-hidden rounded-3xl border border-white/10 bg-[#0d0e11]">
              <div className="relative h-48 overflow-hidden"><img src={img} alt={t} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" /><div className="absolute inset-0 bg-gradient-to-t from-[#0d0e11]" /><I className="absolute bottom-3 left-4 h-8 w-8 text-[#FEDC19]" /></div>
              <div className="p-5"><h3 className="text-lg font-bold">{t}</h3><p className="mt-2 text-sm text-white/65">{d}</p><span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#FEDC19]">Find out more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span></div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % QUOTES.length), 7000); return () => clearInterval(t); }, []);
  const [q, who, role] = QUOTES[i];
  return (
    <section className="mx-auto max-w-5xl px-5 py-24 text-center" data-testid="testimonials">
      <Quote className="mx-auto h-12 w-12 text-[#DB0000]" />
      <blockquote key={i} className="rise-in mt-6 text-2xl font-bold leading-snug sm:text-3xl">“{q}”</blockquote>
      <p className="mt-6 font-bold text-[#FEDC19]">{who}</p><p className="text-sm text-white/60">{role}</p>
      <div className="mt-6 flex justify-center gap-2">{QUOTES.map((_, n) => <button key={n} onClick={() => setI(n)} aria-label={`Quote ${n + 1}`} data-testid={`quote-dot-${n}`} className={`h-2 rounded-full transition-all ${n === i ? "w-8 bg-[#FEDC19]" : "w-2 bg-white/30"}`} />)}</div>
    </section>
  );
};

const Latest = () => {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  useEffect(() => {
    Promise.all(["event", "talk", "news"].map((k) => api.get("/public/posts", { params: { kind: k, limit: 6 } }))).then(([e, t, n]) => {
      const today = new Date().toISOString().slice(0, 10);
      setEvents([...e.data, ...t.data].filter((p) => p.date >= today).sort((x, y) => x.date.localeCompare(y.date)).slice(0, 3));
      setNews(n.data.slice(0, 3));
    }).catch(() => {});
  }, []);
  return (
    <section className="mx-auto max-w-7xl space-y-16 px-5 py-24" data-testid="home-latest">
      {[["Events & talks", CalendarDays, events, "/events"], ["Latest news", ShoppingBag, news, "/news"]].map(([t, I, items, to]) => items.length > 0 && (
        <div key={t}>
          <div className="flex items-end justify-between gap-4"><Reveal><Eyebrow>{t}</Eyebrow><h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">{t === "Latest news" ? "What our volunteers have been up to" : "Come and meet us"}</h2></Reveal>
            <Link to={to} className="hidden items-center gap-1 text-sm font-bold text-[#FEDC19] sm:inline-flex">See all <ArrowRight className="h-4 w-4" /></Link></div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">{items.map((p, n) => <Reveal key={p.id} delay={n * 90}><PostCard p={p} /></Reveal>)}</div>
        </div>
      ))}
    </section>
  );
};

const Donate = () => (
  <section id="donate" className="relative scroll-mt-20 overflow-hidden" data-testid="home-donate">
    <img src="/site/uk-bike-village.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" /><div className="absolute inset-0 bg-[#DB0000]/85 mix-blend-multiply" /><div className="absolute inset-0 bg-[#0d0e11]/50" />
    <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-24 lg:grid-cols-2">
      <Reveal><Eyebrow>Donate</Eyebrow><h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Keep a blood bike on the road tonight.</h2><p className="mt-5 max-w-lg text-white/85">We receive no government funding. Every penny goes on fuel, servicing and keeping our liveried bikes and blood cars ready for the next call.</p>
        <a href={JUSTGIVING} target="_blank" rel="noreferrer" data-testid="donate-justgiving-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FEDC19] px-7 py-3.5 font-bold text-black transition-transform hover:scale-105"><HeartHandshake className="h-5 w-5" /> Donate via JustGiving</a></Reveal>
      <div className="grid gap-4 sm:grid-cols-2">
        {[["Bank transfer (BACS)", "Barclays Bank PLC · SSCBB Community · Sort code 20-85-46 · Account 63671887. Please email fundraising@ssbbgroup.org.uk so we can thank you."], ["By cheque", "Payable to “Shropshire Staffordshire Cheshire Blood Bikes” and sent to SSCBB, Watling Chambers, West Suite, 214 Watling Street, Cannock WS11 0BD. Add a Gift Aid note if you're a UK taxpayer."], ["Easyfundraising", "Shop online with 3,000+ retailers and they donate to us at no cost to you."], ["Get sponsored", "Running, walking or flying to the moon? Download our sponsor form or set up a JustGiving page."]].map(([t, d], n) => (
          <Reveal key={t} delay={n * 80} className="rounded-2xl border border-white/20 bg-black/30 p-5 backdrop-blur-md"><h3 className="font-bold text-[#FEDC19]">{t}</h3><p className="mt-2 text-sm text-white/85">{d}</p>
            {t === "Easyfundraising" && <a href="https://www.easyfundraising.org.uk/causes/ssbb1/" target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-bold underline">Join for free</a>}
            {t === "Get sponsored" && <a href="https://ssbbgroup.org.uk/wp-content/uploads/2018/10/SSCBB-Sponsor-Form.pdf" target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-bold underline">Sponsor form (PDF)</a>}
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function PublicHome() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const load = () => api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="public-home">
      <Hero stats={stats} />
      <About />
      <Impact stats={stats} />
      <PhotoMarquee />
      <PhotoMarquee reverse images={["/site/slider2-1.jpg", "/site/volunteer6.jpg", "/site/blood-cars.jpg", "/site/volunteer5.jpg", "/site/uk-bikes-hills.jpg", "/site/latest-news.jpg", "/site/volunteer4.jpg", "/site/car-crew.jpg"]} />
      <Roles />
      <Testimonials />
      <Latest />
      <Donate />
    </div>
  );
}
